import { Component, QueryList, ViewChildren, OnInit } from "@angular/core";
import { FormArray, FormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ModalController, ToastController } from "@ionic/angular";
import { debounceTime } from 'rxjs/operators';
import { ViewportScroller } from '@angular/common';

import { ConfirmPage, ConfirmPageParamsModel } from "src/pages/confirm/confirm.component";

import { Workflows } from "src/providers/workflows/workflows";
import { CustomNavController } from "src/shared/providers/navigation/custom-nav-controller";
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WorkflowAiReviewService, WorkflowReview } from "src/shared/services/workflow-ai-review.service";

import { ChipPlaceholder, ChipTextArea, InputFocusOut } from "src/shared/components/chip-text-area/chip-text-area";
import { Loading } from 'src/shared/components/loading/loading';
import { ActionModel, InputModel, RecommendedWorkflow, TriggerModel } from "src/models/workflow.model";
import { FieldOption, FormValues } from "../filter-nested-form/filter-nested-form.interface";
import { WORKFLOW_TAGS, getImpliedTagForTriggerCategory, getWorkflowTagsForClient } from "src/shared/constants/workflowTags";
const ALL_OPTIONS_VALUE = '';

import { WorkflowsPage } from 'src/pages/workflows/workflows';
import { Util } from "src/shared/util/util";
import { Client } from "src/providers/client/client";
import { GuestReservations } from "src/providers/guest-reservations/guest-reservations";
import { ActiveIntegrationsPage } from "src/pages/integrations/active-integrations/active-integrations";

interface EntityType {
  id: string;
  name: string;
  url: string;
  default_payload: any;
  viewValue?: string;
  value?: string;
  data: any;
  description: string;
  entity_key: string;
  http_method: string;
  label: string;
}

interface ActionInput {
  key: string;
  options?: EntityType[];
}

interface Action {
  id: string;
  key: string;
  inputs: ActionInput[];
  inputData?: any[];
}

@Component({
    templateUrl: 'edit-workflow.html',
    styleUrls: ['edit-workflow.scss'],
    standalone: false
})

export class EditWorkflowPage implements OnInit {

  actionsForms: FormArray = new FormArray([]);
  allActions: any;
  allActionsData: any;
  allTriggers: any;
  allTriggersData: any;
  allOutputs: any;
  dontUpdateOutputs: boolean;
  dialogParams: any = {};
  publishedVersionIsLatestVersion: boolean;
  didMakeNewDraft: boolean;
  errorMessage: string;
  filteredActions: any;
  filteredTriggers: any;
  isAddingWorkflow: boolean;
  isSavingDraft: boolean;
  isUpdatingWorkflow: boolean;
  isCreatingWorkflowVersion: boolean;
  isPublishing: boolean;
  isRightSideContent: boolean;
  latestVersion: any;
  loaded: boolean;
  paramForms: any = {};
  searchForm: FormGroup;
  selectedTrigger: any;
  selectedActions: any[] = [];
  submitted: boolean;
  triggerInputsLoaded: boolean;
  workflowForm: FormGroup;
  workflowVersionForm: FormGroup;
  workflowId: string;
  radioItems: any;
  selectedChipTextAreaId: string;
  selectedTriggerOutputs: any[] = [];
  recommendedWorkflowIndex: number;
  recommendedWorkflows: RecommendedWorkflow[] = []
  emailSuffix = '';
  webhookUrl = '';
  workflowTagItems = getWorkflowTagsForClient(localStorage.getItem('isRentalClient') === 'true');
  private userEditedFirstTag = false;
  private impliedTag: string | null = null;
  private impliedSlotActive = false;
  private systemTags: string[] = [];

  @ViewChildren(ChipTextArea) chipTextAreaComponents!: QueryList<ChipTextArea>
  @ViewChildren('tidySelect') tidySelectComponents!: QueryList<any>


  boundPublishWorkflowVersion = this.publishWorkflowVersion.bind(this);

constructor(
    private formBuilder: UntypedFormBuilder,
    private modalCtrl: ModalController,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private route: ActivatedRoute,
    private storage: TidyStorage,
    private workflowProvider: Workflows,
    private viewScroller: ViewportScroller,
    private toastCtrl: ToastController,
    private util: Util,
    private client: Client,
    private guestReservations: GuestReservations,
    private aiReviewService: WorkflowAiReviewService
  ) {
    this.workflowForm = this.formBuilder.group({
      name: ['', Validators.required],
      tags: this.formBuilder.array([this.formBuilder.control('')])
    });
    this.workflowVersionForm = this.formBuilder.group({
      trigger_data: this.formBuilder.group({
        id: ['', Validators.required],
        name: [''],
        description: [''],
        input_values: this.formBuilder.group({}),
        data: this.formBuilder.group({})
      }),
      actions_data: this.formBuilder.array([])
    });
    this.searchForm = this.formBuilder.group({
      title: ''
    });
  }

  get tagsFormArray(): FormArray {
    return this.workflowForm.get('tags') as FormArray;
  }

  // The tags to PERSIST on the workflow. The implied slot (first select,
  // auto-filled from the trigger category) is display-only: it is excluded
  // unless the user changed it to something else, which makes it explicit.
  getTagsValue(): string[] {
    const rawValues: string[] = this.tagsFormArray.value.map((tag: string) => (tag || '').trim());
    const persisted = (this.impliedSlotActive && rawValues[0] === this.impliedTag)
      ? rawValues.slice(1)
      : rawValues;
    return persisted
      .filter((tag: string) => !!tag)
      .filter((tag: string, idx: number, arr: string[]) => arr.indexOf(tag) === idx);
  }

  addTag(): void {
    this.tagsFormArray.push(this.formBuilder.control(''));
  }

  removeTag(index: number): void {
    if (index === 0 || this.tagsFormArray.length <= 1) {return;}
    this.tagsFormArray.removeAt(index);
  }

  onTagChange(index: number): void {
    if (index === 0) {
      if (this.impliedSlotActive && this.tagsFormArray.at(0)?.value !== this.impliedTag) {
        // The user overrode the implied slot — their choice becomes an explicit
        // (persisted) tag. The implied tag reappears first on next load.
        this.impliedSlotActive = false;
      }
      this.userEditedFirstTag = true;
    }
  }

  // Shows the tag implied by the trigger's category in the first select.
  // Display-only: the implied slot is excluded from getTagsValue() while
  // unchanged, so it is never persisted on the workflow.
  private applyDefaultTagFromTrigger(category?: string): void {
    let implied = getImpliedTagForTriggerCategory(category);
    // Only show the implied slot when the tag is selectable for this account
    // type — e.g. 'bills' isn't an option on residential accounts.
    if (implied && !this.workflowTagItems.some(item => item.value === implied)) {
      implied = null;
    }
    if (!implied) {
      if (this.impliedSlotActive) {
        if (this.tagsFormArray.length > 1) {
          this.tagsFormArray.removeAt(0, { emitEvent: false });
        } else {
          this.tagsFormArray.at(0).setValue('', { emitEvent: false });
        }
        this.impliedSlotActive = false;
      }
      this.impliedTag = null;
      return;
    }
    this.impliedTag = implied;
    const firstControl = this.tagsFormArray.at(0);
    const firstValue = (firstControl?.value || '').trim();
    if (this.impliedSlotActive || !firstValue || firstValue === implied) {
      firstControl.setValue(implied, { emitEvent: false });
    } else {
      this.tagsFormArray.insert(0, this.formBuilder.control(implied), { emitEvent: false });
    }
    this.impliedSlotActive = true;
  }

  private patchTagsForm(tags: string[] | undefined | null): void {
    const userTagSet = new Set(WORKFLOW_TAGS.map(t => t.value));
    const allTags = tags || [];
    this.systemTags = allTags.filter(t => !userTagSet.has(t));
    const userTags = allTags.filter(t => userTagSet.has(t));
    this.impliedTag = null;
    this.impliedSlotActive = false;
    if (!userTags.length) {return;}
    while (this.tagsFormArray.length > 0) {
      this.tagsFormArray.removeAt(0, { emitEvent: false });
    }
    userTags.forEach((tag) => {
      this.tagsFormArray.push(this.formBuilder.control(tag), { emitEvent: false });
    });
    this.userEditedFirstTag = true;
  }

  @Loading('', true)
  async ngOnInit() {
    try {
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      if (this.isRightSideContent) {
        this.dialogParams = await this.storage.retrieve('dialog-params');
      }
      const navParams = this.navCtrl.getParams(false);
      await this.getAllTriggersAndActions();
      this.workflowId = parseInt(this.route.snapshot.paramMap.get('workflowId')) || await this.storage.retrieve('workflowId');
      if (this.workflowId === 'new') {
        await this.createNewWorkflow();
      } else {
        await this.patchWorkflowForm();
      }
      this.initiateAutoSave();
      const pageTitle = this.getPageTitle();
      this.rightSidePanelService.setDialogPageTitle(pageTitle);
      this.loaded = true;
      this.recommendedWorkflows = this.workflowProvider.getRecommendedWorkflows();
      this.recommendedWorkflowIndex = Number(navParams?.recommendedWorkflowIndex || this.dialogParams?.recommendedWorkflowIndex);
      const recommendedWorkflow = this.recommendedWorkflows[this.recommendedWorkflowIndex];
      if (recommendedWorkflow) {
        this.onSelectRecommendedWorkflow(recommendedWorkflow);
      }
      const me = await this.client.getMe();
      this.emailSuffix = me?.customer_account?.workflow_inbound_email_suffix || '';
      this.webhookUrl = me?.customer_account?.workflow_webhook_url || '';
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  setSelectedChipTextArea(id: string): void {
    this.selectedChipTextAreaId = id;
    if (this.selectedTrigger && this.selectedTrigger.inputs?.some(input => input.key === id && input.type === 'json')) {
      this.initializeTriggerOutputs();
    }
  }

  async getAllTriggersAndActions() {
    this.allTriggers = await this.workflowProvider.getWorkflowTriggerTypes();
    const triggerResult = this.allTriggers.reduce((acc, obj) => {
      const key = obj['category'];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
    this.allTriggers = Object.keys(triggerResult).map(key => ({ category: key, data: triggerResult[key] }));
    this.allTriggersData = [];
    this.allTriggers.map((trigger) => {
      trigger.data.map((data) => {
        this.allTriggersData.push(data);
      });
    });
    this.filteredTriggers = this.allTriggers;
    this.allActions = await this.workflowProvider.getWorkflowActionTypes();
    const actionResult = this.allActions.reduce((acc, obj) => {
      const key = obj['category'];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
    this.allActions = Object.keys(actionResult).map(key => ({ category: key, data: actionResult[key] }));
    this.allActionsData = [];
    this.allActions.map((trigger) => {
      trigger.data.map((data) => {
        this.allActionsData.push(data);
      });
    });
    this.filteredActions = this.allActions;
  }

  async createNewWorkflow() {
    this.isAddingWorkflow = true;
    const payload = {
      'name': `Workflow ${  await this.getWorkflowNumber()}`,
      'description': '',
      'tags' :[]
    };
    const workflow = await this.workflowProvider.createWorkflow(payload);
    await this.workflowProvider.pauseWorkflow(workflow.id);
    this.workflowId = workflow.id;
    this.workflowForm.patchValue({
      name: `Workflow ${  this.workflowId}`
    });
    localStorage.setItem('newWorkflowId', this.workflowId);
  }

  async getWorkflowNumber() {
    const numberOfWorkflows = this.navCtrl.getParam('numberOfWorkflows') || this.dialogParams?.numberOfWorkflows;
    if (numberOfWorkflows) {
      return numberOfWorkflows
    } else {
      const workflows = await this.workflowProvider.getWorkflows();
      return workflows.length;
    }
  }

  async patchWorkflowForm() {
    const workflow = this.navCtrl.getParam('workflow') || this.dialogParams?.workflow || await this.workflowProvider.getWorkflowWithVersions(this.workflowId);
    this.workflowForm.patchValue({
      name: workflow.name
    });
    this.patchTagsForm(workflow?.tags);
    if (workflow?.published_version) {
      this.publishedVersionIsLatestVersion = workflow?.draft_version ? (workflow.draft_version.id < workflow.published_version.id) : true;
    }
    if (this.publishedVersionIsLatestVersion) {
      this.latestVersion = workflow.published_version;
    } else {
      this.latestVersion = workflow.draft_version;
    }
    if (this.latestVersion?.workflow_version_trigger) {
      this.patchTriggerForm(this.latestVersion.workflow_version_trigger);
    }
    if (this.latestVersion?.workflow_version_actions) {
      await this.patchActionsForms(this.latestVersion.workflow_version_actions);
      if (this.latestVersion?.workflow_version_trigger) {
        this.addOutputsToActions();
      }
    }
  }

  async patchTriggerForm(trigger) {
    this.triggerInputsLoaded = false;
    const triggerType = this.allTriggersData.find((item) => item.id === trigger.trigger_id);
    this.applyDefaultTagFromTrigger(triggerType?.category);
    const triggerData = {
      id: triggerType.id,
      name: trigger.name,
      icon_url: triggerType.icon_url,
      description: triggerType.description,
      inputs: triggerType.inputs
    };
    this.workflowVersionForm.patchValue({
      trigger_data: triggerData
    });
    this.selectedTrigger = triggerData;
    await this.getInputValues();
    const triggerInputsForm = this.workflowVersionForm.get('trigger_data').get('input_values') as FormGroup;
    triggerType?.inputs?.forEach((input) => {
      let value = this.latestVersion?.workflow_version_trigger?.input_values?.[input.key];
      // If the input type is JSON, parse it for display in the text area
      if (input.type === 'json' && value && typeof value === 'string') {
        try {
          // Try to parse as JSON and format it nicely for display
          const parsed = JSON.parse(value);
          value = JSON.stringify(parsed, null, 2);
        } catch (e) {
          // If it's not valid JSON, use it as-is
          value = value;
        }
      }
      const isAllValue = value === undefined || value === null || value?.length === 0;
      value = isAllValue ? ALL_OPTIONS_VALUE : value;
      triggerInputsForm.addControl(input.key, this.formBuilder.control(value, [input.required ? Validators.required : Validators.nullValidator]));
      const label = input.form.label;
      triggerInputsForm.addControl(`${input.key  }-radio`, this.formBuilder.control(isAllValue ? '' : this.getAdjustedLabel(label.toLowerCase()), Validators.nullValidator));
    });
    this.triggerInputsLoaded = true;
    this.forceUpdateForm(triggerType);
    this.patchTriggerChipTextArea(triggerType);
    this.initializeTriggerOutputs();
  }

  initializeTriggerOutputs(): void {
    this.selectedTriggerOutputs = [];

    if (this.selectedActions && this.selectedActions.length > 0) {
      this.selectedActions.forEach((action) => {
        if (action?.outputs && action.outputs.length > 0) {
          action.outputs.forEach((outputGroup) => {
            const existingOutput = this.selectedTriggerOutputs.find(
              (item) => item.title === outputGroup.title
            );
            if (!existingOutput) {
              this.selectedTriggerOutputs.push({
                ...outputGroup,
                showOptions: false
              });
            }
          });
        }
      });
    }

    // Also check if we have allOutputs available (from addOutputsToActions)
    if (this.allOutputs && this.allOutputs.length > 0 && this.selectedTriggerOutputs.length === 0) {
      // If we have outputs but no action outputs yet, create a generic outputs group
      this.selectedTriggerOutputs = [{
        array: this.allOutputs,
        title: '<i>Add outputs from workflow</i>',
        showOptions: false
      }];
    }

    // If we have trigger outputs from latestVersion, add them
    if (this.latestVersion?.workflow_version_trigger?.output_variables &&
        this.latestVersion.workflow_version_trigger.output_variables.length > 0 &&
        this.selectedTriggerOutputs.length === 0) {
      const triggerOutputs = this.removeSomeTextFromResponse(
        this.latestVersion.workflow_version_trigger.output_variables
      );
      if (triggerOutputs.length > 0) {
        this.selectedTriggerOutputs = [{
          array: triggerOutputs,
          title: `<i>Add outputs from the <u>${this.selectedTrigger?.name || 'trigger'}</u> trigger</i>`,
          showOptions: false
        }];
      }
    }
  }

  forceUpdateForm(triggerType) {
    setTimeout(() => {
      const selectComponentsToArray = this.tidySelectComponents.toArray();
      selectComponentsToArray.forEach((selectComponent) => {
        triggerType?.inputs?.forEach((input) => {
          const label = input.form.label.toLowerCase().includes('address') ? 'Properties' : input.form.label;
          if (label === selectComponent.label) {
            selectComponent.items = input.items
              .map((item) => ({
                  viewValue: item.viewValue,
                  value: item.value
                }));
            selectComponent.listedItems = selectComponent.items;
            if (this.latestVersion?.workflow_version_trigger?.input_values?.[input.key]) {
              const responseValue = this.latestVersion?.workflow_version_trigger?.input_values?.[input.key];
              selectComponent.form.patchValue(responseValue);
            }
          }
        })
      })
    }, 200)
  }

  async patchActionsForms(actions) {
    this.actionsForms = this.formBuilder.array([]);
    this.selectedActions = [];
    const normalize = (value, inputType) => {
      if (inputType?.type === 'integer') {
        return parseInt(value.toString());
      }
      return value;
    }

    const loadInputs = (actionData, actionType) => {
      const inputs = Object.entries(actionData.input_values)
      const inputsForms = this.formBuilder.group({})
      inputs.forEach((input) => {
        const key = input[0];
        const inputType = actionType.inputs.find((inp) => inp.key === key);
        const value = normalize(input[1], inputType);
        const validator = actionType.required ? Validators.required : null;
        const control = this.formBuilder.control(value, validator)
        inputsForms.addControl(key, control)
      })
      return inputsForms;
    }
    await Promise.all(actions.map(async (action) => {
      const actionType = this.allActionsData.find((item) => item.id === action.action_id);
      const formId = Math.random().toString(36).substring(7);
      const actionForm = {
        formId: [formId],
        id: [actionType.id, Validators.required],
        name: [action.name, Validators.required],
        description: [action.description],
        key: [actionType.key],
        input_values: loadInputs(action, actionType),
        icon_url: actionType.icon_url
      };
      const actionFormGroup = this.formBuilder.group(actionForm);
      this.actionsForms.push(actionFormGroup);
      // Pass the saved receiver_type so the BE returns contextualized
      // input_options / input_default_value (e.g. the populated team-member
      // list when receiver_type is customer_member). Without this, editing
      // a previously-saved Team Member workflow leaves to_customer_members
      // hidden because its default stays {{hidden_input}}.
      const savedReceiverType = action?.input_values?.receiver_type;
      const inputDataPayload = savedReceiverType
        ? JSON.stringify({ receiver_type: savedReceiverType })
        : undefined;
      let inputData = await this.workflowProvider.getInputData(actionType.id, 'Action', inputDataPayload);
      const inputOptions = await this.getInputOptions(inputData, action);
      actionType?.inputs?.forEach((input) => {
        (actionFormGroup.controls.input_values as FormGroup).addControl(input.key, this.formBuilder.control('', input.required ? Validators.required : null));
        // Bypass getActionSelectOptions' input.options early-return: the
        // cached allActionsData entry may carry stale options from a prior
        // load with a different receiver_type.
        const inputDataItem = inputData?.find((d) => d?.input_key === input?.key);
        input.options = inputDataItem
          ? (inputDataItem.input_options || []).map((option) => ({
              ...option,
              value: option.value,
              viewValue: option.label,
            }))
          : (input.options || []);
      });
      const entityTypeInput = inputData.find((data) => data?.input_key === 'entity_type_id');
      const guestReservationSourceIdValue = action?.input_values?.guest_reservation_source_id;
      const entityTypeIdValue = action?.input_values?.entity_type_id;
      if (entityTypeInput && guestReservationSourceIdValue && guestReservationSourceIdValue !== '' && guestReservationSourceIdValue !== 'add_integration' && entityTypeIdValue) {
        inputData = await this.fetchGuestReservationSourceTypeEntities(guestReservationSourceIdValue, entityTypeIdValue, actionType.id);
      }
      this.selectedActions.push({
        ...actionFormGroup.value,
        inputs: actionType.inputs,
        inputData,
        inputOptions,
      });
      return Promise.resolve();
    }));
    this.selectedActions = this.actionsForms.value.map(value => {
      const formId = value.formId;
      return this.selectedActions.find(action => action.formId === formId);
    });
    this.addOutputsToActions();
    setTimeout(() => {
      this.selectedActions.forEach((action, index) => {
        this.patchChipTextArea(
          action?.formId,
          this.actionsForms?.value?.[index]?.input_values
        )
      })
    }, 1000);
  }

  patchChipTextArea(formId: string, input_values: any): void {
    const chipTextAreaComponents = this.chipTextAreaComponents
      .filter((component) => component.id === formId)
    if (!formId || !input_values) {
      return;
    }

    chipTextAreaComponents.forEach(chipTextAreaComponent => {
    chipTextAreaComponent.body = {
      content: this.parseBody(input_values[chipTextAreaComponent.inputKey]),
      chips: this.parseOutputs()
    }
    chipTextAreaComponent.parseAndSetBody()
    })
  }

  patchTriggerChipTextArea(triggerType: any): void {
    setTimeout(() => {
      const triggerInputsForm = this.workflowVersionForm.get('trigger_data').get('input_values') as FormGroup;
      if (!triggerInputsForm) {
        return;
      }

      const chipTextAreaComponents = this.chipTextAreaComponents
        .filter((component) => component.id === 'trigger');

      chipTextAreaComponents.forEach(chipTextAreaComponent => {
        const inputKey = chipTextAreaComponent.inputKey;
        const value = triggerInputsForm.get(inputKey)?.value || '';
        chipTextAreaComponent.body = {
          content: value,
          chips: []
        };
        chipTextAreaComponent.parseAndSetBody();
      });
    }, 200);
  }

  parseOutputs(): ChipPlaceholder[] {
    if (!this.allOutputs?.length) {
      return [];
    }
    return this.allOutputs.map((output) => ({
        text: output.plain_title ? output.plain_title : output.title,
        value: output.key
      }))
  }

  parseBody(body: string, removeNotFoundOutputs = false): string {
    if (!body) {
      return '';
    }
    const regex = /{([^}]+)}}/g;
    const matches = body.match(regex);
    if (!matches) {
      return body;
    }
    const parsedBody = matches.reduce((acc, match) => {
      const foundOutput = this.allOutputs.find((output) => output.key === match);
      if (foundOutput) {
        return acc.replace(match, foundOutput.key);
      } else {
        if (removeNotFoundOutputs) {
          return acc.replace(match, '');
        }
      }
      return acc;
    }, body);
    return parsedBody;
  }

  initiateAutoSave() {
    this.isCreatingWorkflowVersion = true;
    setTimeout(() => this.isCreatingWorkflowVersion = false, 3000);
    this.workflowForm.valueChanges.pipe(
      debounceTime(1000)
    ).subscribe(_value => this.updateWorkflowTitle());
    this.workflowVersionForm.valueChanges.pipe(
      debounceTime(1000)
    ).subscribe((_value) => {
      this.createWorkflowVersion()
    });
    this.actionsForms.controls.map((form) => {
      form.valueChanges.pipe(
        debounceTime(1000)
      ).subscribe(_value => this.createWorkflowVersion());
    });
  }

  onRadioOptionChange(event: any, inputForm: FormGroup): void {
    if (event.value === '') {
      inputForm.reset();
    }
  }

  async updateWorkflowTitle() {
    if (this.isUpdatingWorkflow) {
      return;
    }
    this.isSavingDraft = true;
    this.isUpdatingWorkflow = true;
    try {
      const freshWf = await this.workflowProvider.getWorkflow(this.workflowId).catch(() => null);
      const existingTags = freshWf?.tags || this.systemTags;
      const userTagSet = new Set(WORKFLOW_TAGS.map(t => t.value));
      // Preserve whatever system tags the record already has (e.g. 'ai_agent').
      // Deliberately not inferred from the workflow name — that fabricated the
      // tag on records that never had one and broke when an agent was renamed.
      const freshSystemTags = (existingTags || []).filter(t => !userTagSet.has(t));
      const name = this.workflowForm.value.name;
      const payload = {
        name,
        'description': '',
        'tags': [...freshSystemTags, ...this.getTagsValue()]
      }
      await this.workflowProvider.updateWorkflow(payload, this.workflowId);
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
    setTimeout(() => this.isSavingDraft = false, 1000);
    await this.storage.save('shouldReloadAllAutomations', true);
    this.didMakeNewDraft = true;
    this.isUpdatingWorkflow = false;
  }

  async createWorkflowVersion(actionsPayload = null) {
    if (this.isCreatingWorkflowVersion) {
      return;
    }
    this.isSavingDraft = true;
    this.isUpdatingWorkflow = true;
    this.dialogParams.workflow = null;
    await this.storage.save('dialog-params', this.dialogParams);
    const didAddTrigger = this.workflowVersionForm.value.trigger_data.id;
    if (didAddTrigger) {
      const newVersionPayload = {
        workflow_id: Number(this.workflowId),
        trigger_data: {
          id: this.workflowVersionForm.value.trigger_data.id,
          name: this.workflowVersionForm.value.trigger_data.name,
          description: this.workflowVersionForm.value.trigger_data.description
        },
      }
      let didAddTriggerInputValues = false;
      const inputValues = Object.entries(this.workflowVersionForm.value.trigger_data.input_values).reduce((newObj, [key, value]) => {
        if (value !== ALL_OPTIONS_VALUE || (value !== ALL_OPTIONS_VALUE && key !== 'address_ids')) {
          const objValue = value as any;
          newObj[key] = Array.isArray(objValue) && objValue?.includes(ALL_OPTIONS_VALUE) ? null : value;
          const foundInput = this.selectedTrigger?.inputs?.find((input) => input.key === key);
          if (foundInput?.form?.type === 'number' && newObj[key] !== null) {
            newObj[key] = Number(newObj[key]);
          }
          if (foundInput?.type === 'json' && newObj[key] !== null && newObj[key] !== '') {
            const stringValue = newObj[key] as string;
            const cleanValue = stringValue.replace(/<[^>]*>/g, '').trim();

            if (cleanValue === '') {
              newObj[key] = null;
            } else {
              try {
                const parsed = JSON.parse(cleanValue);
                newObj[key] = JSON.stringify(parsed);
              } catch (e) {
                console.warn(`Invalid JSON for ${key}, attempting to fix:`, cleanValue);
                try {
                  const trimmed = cleanValue.trim();
                  const parsed = JSON.parse(trimmed);
                  newObj[key] = JSON.stringify(parsed);
                } catch (e2) {
                  newObj[key] = JSON.stringify(cleanValue);
                }
              }
            }
          }
          didAddTriggerInputValues = true;
        }
        return newObj;
      }, {});
      if (didAddTriggerInputValues) {
        newVersionPayload.trigger_data['input_values'] = this.removeNotMatchedInputs(inputValues);
      }
      if (this.latestVersion) {
        // if the trigger is the same as the latest version, use the latest version's trigger id
        // if the trigger is different, use the new trigger's id that will be created
        if (newVersionPayload.trigger_data['id'] === this.latestVersion.workflow_version_trigger.trigger_id) {
          newVersionPayload.trigger_data['step_id'] = this.latestVersion.workflow_version_trigger.step_id
        }
      }
      const didAddAction = this.actionsForms.value.length > 0;
      if (didAddAction && !actionsPayload) {
        const filteredActions = this.actionsForms.value.filter((action) => action.id !== '');
        newVersionPayload['actions_data'] = this.parseActions(filteredActions);
      } else {
        newVersionPayload['actions_data'] = actionsPayload;
      }
      newVersionPayload['actions_data']?.forEach((action) => {
        if (action?.input_values && 'attachments' in action.input_values && !Array.isArray(action.input_values.attachments)) {
          action.input_values.attachments = [];
        }
      });
      this.latestVersion = await this.workflowProvider.createWorkflowVersion(newVersionPayload);
      this.addOutputsToActions();
    }
    await this.storage.save('shouldReloadAllAutomations', true);
    setTimeout(() => this.isSavingDraft = false, 1000);
    this.publishedVersionIsLatestVersion = false;
    this.didMakeNewDraft = true;
    this.isUpdatingWorkflow = false;
  }

  removeNotMatchedInputs(inputValues) {
    const availableInputKeys = this.selectedTrigger?.inputs?.map((input) => input.key);
    return Object.entries(inputValues).reduce((newObj, [key, value]) => {
      if (availableInputKeys.includes(key)) {
        newObj[key] = value;
      }
      return newObj;
    }, {});
  }

  addOutputsToActions() {
    if (this.dontUpdateOutputs) {
      return;
    }
    const triggerOutputs = this.removeSomeTextFromResponse(this.latestVersion.workflow_version_trigger.output_variables);
    this.allOutputs = [
      ...triggerOutputs
    ]
    this.dontUpdateOutputs = false;
    this.latestVersion.workflow_version_actions.map((action, versionIndex) => {
      this.selectedActions[versionIndex]['outputs'] = [{
        array: triggerOutputs,
        title: `<i>Add outputs from the <u>${  this.latestVersion.workflow_version_trigger.name  }</u> trigger</i>`,
        showOptions: false
      }];
      this.selectedActions[versionIndex]['parsedOutputs'] = this.getParsedOutputs(this.selectedActions[versionIndex]['outputs']);
      if (versionIndex !== 0) {
        this.latestVersion.workflow_version_actions.map((act, actionIndex) => {
          if (versionIndex > actionIndex && act.output_variables.length > 0) {
            const actionOutputs = this.removeSomeTextFromResponse(act.output_variables);
            this.allOutputs.concat(actionOutputs);
            this.selectedActions[versionIndex]['outputs'].push({
              array: actionOutputs,
              title: `<i>Add outputs from the <u>${  act.name  }</u> action</i>`,
              showOptions: false
            });
            this.selectedActions[versionIndex]['parsedOutputs'] = this.getParsedOutputs(this.selectedActions[versionIndex]['outputs']);
          }
        });
      }
    });
    this.initializeTriggerOutputs();
  }

  removeSomeTextFromResponse(outputs) {
    outputs.map((output) => {
      if (output.title?.includes('Event')) {
        output.title = output.title.replace('Event.', '');
      }
      if (output.title?.includes('Model: Job')) {
        output.title = output.title.replace('Model: Job.', '');
      }
      if (output.title?.includes('object')) {
        output.title = null;
      }
    });
    const filteredOutputs = outputs.filter((output) => output.title !== null)
    return filteredOutputs;
  }

  parseActions(actions: any[]): any[] {
    return actions.map((action, index) => {
      const { _formId, ...rest } = action

      // Get the full action object from selectedActions to get the key and other properties
      const selectedAction = this.selectedActions[index];

      // If selectedAction doesn't have key, try to find by formId instead
      let actionKey = selectedAction?.key;
      if (!actionKey) {
        const actionByFormId = this.selectedActions.find(a => a?.formId === action.formId);
        actionKey = actionByFormId?.key;
      }

      const payload = {
        ...rest,
        // Include the key from the selected action
        key: actionKey,
        input_values: action?.input_values || {}
      }
      if (action?.input_values?.filter_groups) {
        payload['input_values']['filter_groups'] = action.input_values.filter_groups;
      }
      if (this.latestVersion?.workflow_version_actions[index]) {
        // if the action is the same as the latest version, use the latest version's action id
        // if the action is different, use the new action's id that will be created
        if (this.latestVersion.workflow_version_actions[index].action_id === action.id) {
          payload['step_id'] = this.latestVersion.workflow_version_actions[index]?.step_id;
        }
      }

      const foundChipTextArea = this.chipTextAreaComponents.filter((component) => component.id === action.formId);
      foundChipTextArea.forEach(input => {
        if (input.innerHtml) {
          payload['input_values'][input.inputKey] = input.innerHtml
        }
      });

      if ('attachments' in payload.input_values && !Array.isArray(payload.input_values.attachments)) {
        payload.input_values.attachments = [];
      }

      return payload;
    });
  }

  async publishWorkflowVersion() {
    if (this.isPublishing) {
      return;
    }
    this.submitted = true;
    if (!this.workflowForm.valid) {
      return;
    }
    const hasNoTriggerOrAction = !this.selectedTrigger || !this.selectedActions[0]?.id;
    if (hasNoTriggerOrAction) {
      return this.showError('Please add a trigger and action to this workflow before publishing.');
    }

    try {
      this.isPublishing = true;

      if (this.hasNotFilledRequiredChipTextArea()) {
        this.handleNotFilledRequiredChipTextArea();
        return;
      }

      // Parse actions to include ChipTextArea content before review
      const parsedActions = this.parseActions(this.actionsForms.value);

      // Show AI review modal before publishing
      const review = await this.aiReviewService.reviewWorkflow({
        name: this.workflowForm.value.name,
        trigger: this.selectedTrigger,
        actions: parsedActions
      });

      await this.showPublishReviewModal(review);
    } catch (err) {
      if (!this.latestVersion?.id) {
        this.errorMessage = 'Please add a trigger and action to this workflow before publishing.';
        this.showError(this.errorMessage);
        return;
      }
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    } finally {
      this.isPublishing = false;
    }
  }

  /**
   * Show modal with AI review results before publishing
   */
  private async showPublishReviewModal(review: WorkflowReview): Promise<void> {
    const hasIssues = review.hasIssues;

    const title = hasIssues ? 'Issues Found' : review.hasSuggestions ? 'AI Review' : 'Ready to Publish';

    // Use bodyMarkdown for proper markdown rendering via tidy-markdown component
    const bodyMarkdown = review.items.length > 0
      ? review.items[0].suggestion
      : 'Workflow looks good! No issues or suggestions.';

    const confirmText = hasIssues ? 'Publish Anyway' : 'Publish';
    const backText = hasIssues ? 'Go Back & Fix' : 'Review & Edit';

    const reviewModal: ConfirmPageParamsModel = {
      title,
      body: '', // Not used when bodyMarkdown is provided
      bodyMarkdown,
      backText,
      confirmText,
      confirmAction: async () => {
        this.modalCtrl.dismiss();
        await this.performPublish();
      },
      customBackAction: async () => {
        this.modalCtrl.dismiss();
      }
    };

    const modal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: reviewModal,
      animated: false,
      cssClass: 'tidy-transparent-modal'
    });

    await modal.present();
  }

  /**
   * Actually perform the publish after review
   */
  private async performPublish(): Promise<void> {
    try {
      await this.workflowProvider.publishWorkflow(this.latestVersion.id);
      await this.workflowProvider.unpauseWorkflow(this.workflowId);
      const cameFromActiveIntegrations = this.navCtrl.getParam('cameFromActiveIntegrations') || this.dialogParams?.cameFromActiveIntegrations;
      if (cameFromActiveIntegrations) {
        this.goToPage('automations/active-integrations', {}, ActiveIntegrationsPage);
      } else {
        this.goToPage('automations/workflows', {}, WorkflowsPage);
      }
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(this.errorMessage);
    }
  }

  async showError(message: string): Promise<void> {
    this.util.showError(message);
  }

  hasNotFilledRequiredChipTextArea(): boolean {
    const chipTextAreaComponents = this.chipTextAreaComponents?.toArray();
    if (!chipTextAreaComponents) {
      return false;
    }
    const notFilledRequiredChipTextArea = chipTextAreaComponents.filter((component) => component.isRequired && (!component.innerHtml || component.innerHtml === ''))
    return notFilledRequiredChipTextArea.length > 0;
  }

  handleNotFilledRequiredChipTextArea(): void {
    const chipTextAreaComponents = this.chipTextAreaComponents?.toArray();
    if (!chipTextAreaComponents) {
      return;
    }
    const notFilledRequiredChipTextArea = chipTextAreaComponents.filter((component) => component.isRequired && (!component.innerHtml || component.innerHtml === ''))
    notFilledRequiredChipTextArea.forEach((component) => {
      component.control.markAsTouched();
    })
    const anchorId = notFilledRequiredChipTextArea[0]?.id;
    if (anchorId) {
      this.viewScroller.scrollToAnchor(anchorId);
    }
  }

  get actionsData(): FormArray {
    return this.workflowVersionForm.get('actions_data') as FormArray;
  }

  getActionForm(index: number): FormGroup {
    return this.actionsForms.at(index) as FormGroup;
  }

  shouldDisplayTriggerFiltersText(): boolean {
    return this.triggerInputsLoaded && this.selectedTrigger?.inputs?.length && this.selectedTrigger?.inputs?.some((input) => input?.form?.type === 'multi_select' || input?.form?.type === 'select');
  }

  copyEmail(email: string): void {
    this.util.copyToClipBoard(email + this.emailSuffix);
  }

  copyWebhook(token: string): void {
    // Concatenate webhook base URL with token
    const fullWebhookUrl = this.webhookUrl + token;
    this.util.copyToClipBoard(fullWebhookUrl);
  }

  generateRandomToken(length = 5): string {
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  }

  getFullWebhookUrl(token: string): string {
    return this.webhookUrl + token;
  }

  @Loading('', true)
  async selectTrigger(trigger) {
    this.triggerInputsLoaded = false;
    if (trigger.name === 'Request Trigger') {
      return this.rightSidePanelService.navigateTo('automations/feature-request', { featureType: 'trigger', recommendedWorkflowIndex: this.recommendedWorkflowIndex });
    }
    if (trigger.coming_soon) {
      return this.rightSidePanelService.navigateTo('automations/feature-request', { feature: trigger, featureType: 'trigger', recommendedWorkflowIndex: this.recommendedWorkflowIndex });
    }
    this.workflowVersionForm.patchValue({
      trigger_data: {
        id: trigger.id,
        name: trigger.name,
        input_values: {},
        data: {}
      }
    });
    const triggerInputsForm = this.workflowVersionForm.get('trigger_data').get('input_values') as FormGroup;

    trigger?.inputs?.forEach((input) => {
      const defaultValue = this.getDefaultInputValue(input);
      triggerInputsForm.addControl(input.key, this.formBuilder.control(defaultValue, input.required ? Validators.required : null ));
      triggerInputsForm.addControl(`${input.key  }-radio`, this.formBuilder.control('', null));
    });

    const tokenInput = triggerInputsForm?.controls['token'];
    if (tokenInput) {
      // Generate a random 5-character alphanumeric token
      const randomToken = this.generateRandomToken(5);
      tokenInput.patchValue(randomToken);
    }

    this.selectedTrigger = trigger;
    this.applyDefaultTagFromTrigger(trigger?.category);
    await this.getInputValues();
    if (this.selectedActions.length === 0) {
      this.addAction();
    }
    this.triggerInputsLoaded = true;
    this.isCreatingWorkflowVersion = false;
    await this.createWorkflowVersion();
  }

  getDefaultInputValue(input) {
    if (input.key === 'timezone') {
      return 'America/Los_Angeles';
    } else if (input.key === 'trigger_on_weekends') {
      return true;
    } else if (input.key === 'time_of_day') {
      return 12;
    } else if (input.key === 'day_of_the_week') {
      return 0;
    } else if (input.key === 'date_of_the_month') {
      return 1;
    }
  }

  async getInputValues() {
    const hasSelect = this.selectedTrigger.inputs.find((input) => input.form.type === 'select' || input.form.type === 'multi_select' || input.key === 'assigned_to_concierge');
    if (hasSelect) {
      const inputData = await this.workflowProvider.getInputData(this.selectedTrigger.id, 'Trigger');
      inputData.map((data) => {
        this.selectedTrigger.inputs.map((input) => {
          if (data.input_key === input.key) {
            input['items'] = data.input_options.map((option) => ({
              value: option.value,
              viewValue: option.label
            }));

            const allOptionsLabel = (label) => {
              const foundLabel = {
                'Categories': 'Trigger on all categories',
                'Addresses': 'Trigger on all properties',
                'Address': 'Trigger on all properties',
                'Service Types': 'Trigger on all services',
                'Service Type': 'Trigger on all services',
                'Issue Type': 'Trigger on all issues',
                'Job State': 'Trigger on all job states',
                'Assigned to Concierge?': 'No filter',
                'Only trigger on scheduled or in progress jobs': 'No filter',
              }?.[label];
              return foundLabel ?? `All ${label}`;
            }

            this.addRadioItems(input.form.label, allOptionsLabel(input.form.label))

            if (!input.required && input.form.type !== 'multi_select' && input.key !== 'assigned_to_concierge') {
              input['items'].unshift({
                value: ALL_OPTIONS_VALUE,
                viewValue: allOptionsLabel(input.form.label)
              });
            }
          }
        })
      });
    }
  }

  addRadioItems(label: string, allOptionsLabel: string): void {
    if (this.radioItems && this.radioItems?.[label]) {
      return;
    }
    const adjustedLabel = this.getAdjustedLabel(label.toLowerCase());
    this.radioItems = {
      ...this.radioItems,
      [label]: [
        {
          value: ALL_OPTIONS_VALUE,
          viewValue: allOptionsLabel
        },
        {
          value: adjustedLabel,
          viewValue: adjustedLabel
        },
      ]
    }
  }

  getAdjustedLabel(label) {
    let adjustedLabel = `Trigger only on some ${  label}`;
    if (label.includes('address')) {
      adjustedLabel = 'Trigger only on some properties';
    } else if (label.includes('state')) {
      adjustedLabel = 'Trigger on specific states';
    } else if (label.includes('assigned to concierge?')) {
      adjustedLabel = 'Trigger only for concierge assigned or not';
    }
    return adjustedLabel;
  }

  async addAction() {
    const actionIndex = this.selectedActions.length;
    const actionForm = {
      formId: [''],
      id: [''],
      name: [''],
      description: [''],
      key: [''],
      input_values: this.formBuilder.group({})
    };
    const actionFormGroup = this.formBuilder.group(actionForm);
    this.actionsForms.insert(actionIndex, actionFormGroup);
    this.selectedActions.push(actionFormGroup.value);
  }

  async selectActionType(newAction, formIndex: number) {
    if (newAction.coming_soon) {
      return this.rightSidePanelService.navigateTo('automations/feature-request', { feature: newAction, featureType: 'action' });
    }
    this.isCreatingWorkflowVersion = false;
    const form = this.getActionForm(formIndex);
    if (!form) {
      return;
    }
    const inputData = await this.workflowProvider.getInputData(newAction.id, 'Action');
    newAction.inputData = inputData || [];
    newAction.inputOptions = await this.getInputOptions(inputData);
    form.patchValue({
      formId: Math.random().toString(36).substring(7),
      id: newAction.id,
      name: newAction.name,
      key: newAction.key,
      description: ''
    });
    newAction?.inputs?.forEach((input) => {
      (form.controls.input_values as FormGroup).addControl(input.key, this.formBuilder.control('', input.required ? Validators.required : null));
      input.options = this.getActionSelectOptions(input, inputData);
    });
    this.selectedActions[formIndex] = newAction;
    this.createWorkflowVersion();
    form.valueChanges.pipe(
      debounceTime(1000)
    ).subscribe(_value => this.createWorkflowVersion());
    setTimeout(() => {
      inputData.forEach((data) => {
        try {
          if (data?.input_default_value) {
            const payloadValue = this.formatJsonString(
              data.input_default_value
            );
            this.updateChipTextArea(formIndex, data.input_key, payloadValue);
          }
        } catch (_error) {
        // Intentionally empty - errors handled elsewhere
        }
      })
    }, 2000);
  }

  getActionSelectOptions(input, inputData) {
    if (input?.options) {
      return input.options || [];
    }
    const guestReservationSourceIdInput = inputData.find((data) => data?.input_key === 'guest_reservation_source_id');
    if (guestReservationSourceIdInput && guestReservationSourceIdInput?.input_options) {
      guestReservationSourceIdInput?.input_options?.unshift({ value: 'add_integration', label: '+ Add Integration'})
    }
    const inputOptions = inputData?.find((data) => data?.input_key === input?.key)?.input_options;
    const parsedInputOptions = inputOptions?.map((option) => ({
      ...option,
      value: option.value,
      viewValue: option.label
    }));
    return parsedInputOptions || [];
  }

  async getInputOptions(inputData: any[], action?: any) {
    if (!inputData) {
      return [];
    }
    const entityTypeInput = inputData.find((data) => data?.input_key === 'entity_type_id');
    const guestReservationSourceIdValue = action?.input_values?.guest_reservation_source_id;
    const entityTypeIdValue = action?.input_values?.entity_type_id;
    if (entityTypeInput && guestReservationSourceIdValue && guestReservationSourceIdValue !== '' && guestReservationSourceIdValue !== 'add_integration' && entityTypeIdValue) {
      action.inputData = await this.fetchGuestReservationSourceTypeEntities(guestReservationSourceIdValue, entityTypeIdValue, action.action_id);
      const entityTypeOptions = this.mapEntitiesToOptions(action.inputData);
      entityTypeInput.options = entityTypeOptions;
      entityTypeInput.input_options = entityTypeOptions;
    }
    const inputOptions = inputData.map((data) => data?.input_options).flat();
    return inputOptions || [];
  }

  searchActions(value) {
    if (value === '') {
      return this.filteredActions = this.allActions;
    }
    this.filteredActions = [];
    const valueLower = value.toLowerCase();
    const actionFilter = (action) => {
      if (action.name.toLocaleLowerCase().includes(valueLower)) {return true;}
      if (action.description.toLocaleLowerCase().includes(valueLower)) {return true;}
    }
    this.filteredActions = this.allActionsData.filter(actionFilter);
  }

  searchTriggers(value) {
    if (value === '') {
      return this.filteredTriggers = this.allTriggers;
    }
    this.filteredTriggers = [];
    const valueLower = value.toLowerCase();
    const triggerFilter = (trigger) => {
      if (trigger.name.toLocaleLowerCase().includes(valueLower)) {return true;}
      if (trigger.description.toLocaleLowerCase().includes(valueLower)) {return true;}
    }
    this.filteredTriggers = this.allTriggersData.filter(triggerFilter);
  }

  changeTrigger() {
    this.selectedTrigger = null;
  }

  async deleteWorkflow() {
    const confirmDelete: ConfirmPageParamsModel = {
      title: 'Delete Workflow?',
      body: 'You cannot undo this action.',
      backText: 'Go Back',
      confirmText: 'Delete',
      confirmAction: async () => {
        this.confirmDeleteWorkflow();
        this.modalCtrl.dismiss();
      }
    };
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: confirmDelete,
      animated: false,
      cssClass: 'tidy-transparent-modal',
    });
    confirmationModal.present();
  }

  async confirmDeleteWorkflow() {
    try {
      await this.workflowProvider.deleteWorkflow(this.workflowId);
      await this.storage.save('shouldReloadAllAutomations', true);
      this.goToPage('automations/workflows', {}, WorkflowsPage);
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }

  forceUpdateOfNextChipTextArea(actionIndex: any): void {
    const foundNextAction = this.getActionForm(actionIndex + 1);
    if (!foundNextAction) {
      return;
    }
    const foundNextChipTextArea = this.chipTextAreaComponents.find((component) => component.id === foundNextAction.get('formId').value)
    const foundCurrentAction = this.getActionForm(actionIndex);
    const foundChipTextArea = this.chipTextAreaComponents.find((component) => component.id === foundCurrentAction.get('formId').value)
    foundChipTextArea.body = {
      content: foundNextChipTextArea.innerHtml,
      chips: foundNextChipTextArea.placeholders
    }
    foundChipTextArea.parseAndSetBody()
  }

  async deleteAction(actionIndex: any) {
    const filteredActions = this.actionsForms.value.filter((action) => action.id !== '');
    const actionsPayload = this.parseActions(filteredActions);
    actionsPayload.splice(actionIndex, 1);
    this.forceUpdateOfNextChipTextArea(actionIndex);
    this.actionsForms.removeAt(actionIndex);
    this.selectedActions.splice(actionIndex, 1);
    const didAddAction = this.actionsForms.value.length > 0;
    if (didAddAction) {
      this.createWorkflowVersion(actionsPayload);
    }
  }

  goToAddConciergeTask() {
    const params = {
      title: this.isAddingWorkflow ? 'Add Workflow' : 'Edit Workflow',
      type: 'support.concierge_task',
      isWorkflow: true,
      workflowName: this.workflowForm.value.name,
      metadata: {
        workflow_id: this.workflowId
      }
    }
    this.rightSidePanelService.navigateTo('contact-concierge', params);
  }

  onInputChange(event: InputFocusOut) {
    if (event.value) {
      this.createWorkflowVersion();
    }
  }

  onTriggerInputChange(event: InputFocusOut, inputKey: string) {
    const triggerInputsForm = this.workflowVersionForm.get('trigger_data').get('input_values') as FormGroup;
    if (triggerInputsForm && triggerInputsForm.get(inputKey)) {
      const foundInput = this.selectedTrigger?.inputs?.find((input) => input.key === inputKey);
      let value = event.value;

      if (foundInput?.type === 'json' && value) {
        value = value.replace(/<[^>]*>/g, '').trim();
      }

      triggerInputsForm.get(inputKey).setValue(value);
      if (value) {
        this.createWorkflowVersion();
      }
    }
  }

  addChipPlaceholder(output: any, actionIndex: string, inputKey: string): void {
    this.dontUpdateOutputs = true;
    const chipTextAreaComponent = this.chipTextAreaComponents.find((component) => component.id === actionIndex && component.inputKey === inputKey);
    const placeholder = {
      text: output.plain_title ? output.plain_title : output.title,
      value: output.key
    }
    chipTextAreaComponent.appendPlaceholder(placeholder)
  }

  toggleShowOutputOptions(actionIndex, outputIndex) {
    this.selectedActions[actionIndex].outputs[outputIndex].showOptions = this.selectedActions[actionIndex].outputs[outputIndex].showOptions ? false : true;
  }

  toggleShowTriggerOutputOptions(outputIndex) {
    if (this.selectedTriggerOutputs[outputIndex]) {
      this.selectedTriggerOutputs[outputIndex].showOptions = this.selectedTriggerOutputs[outputIndex].showOptions ? false : true;
    }
  }

  addTriggerChipPlaceholder(output: any, inputKey: string): void {
    this.dontUpdateOutputs = true;
    const chipTextAreaComponent = this.chipTextAreaComponents.find((component) => component.id === 'trigger' && component.inputKey === inputKey);
    if (chipTextAreaComponent) {
      const placeholder = {
        text: output.plain_title ? output.plain_title : output.title,
        value: output.key
      };
      chipTextAreaComponent.appendPlaceholder(placeholder);
    }
  }

  getTriggerFilterHeaderText() {
    if (this.selectedTrigger?.inputs?.some((input) => input?.form?.type === 'multi_select' || input?.form?.type === 'select')) {
      return 'These can narrow when a trigger fires.'
    }
    if (this.selectedTrigger.name.includes('Every')) {
      return 'Run this trigger on:'
    } else if (this.selectedTrigger.name === 'Job Completed') {
      return 'Filter jobs completed by:'
    } else {
      return 'Filter this trigger by:'
    }
  }

  getFilterNameInSingular(label: string): string {
    if (!label) {
      return '';
    }
    const filterNames = {
      'Categories': 'Category',
      'Addresses': 'Property',
      'Address': 'Property',
      'Service Types': 'Service',
      'Service Type': 'Service',
      'Issue Type': 'Maintenance Task',
      'Assigned to Concierge?': 'Concierge',
    }
    return filterNames[label] || label || ''
  }

  getPageTitle() {
    if (this.isAddingWorkflow) {
      return 'Add Workflow';
    } else {
      return 'Edit Draft';
    }
  }

  getTriggerViewValues(values, label, items) {
    if (values === '') {
      return `All ${  label}`;
    }
    let string = '';
    if (values) {
      values.map((value) => {
        const item = items.find((itm) => itm.value === value);
        string += `${item.viewValue  }, `;
      });
    }
    string = string.slice(0, -2);
    return string;
  }

  async onSelectRecommendedWorkflow(
    workflow: RecommendedWorkflow
  ): Promise<void> {
    await this.foundAndSelectTrigger(workflow.trigger);
    this.foundAndSelectActions(workflow.actions);
    this.patchTriggerInputs(workflow.trigger.inputs);
    setTimeout(() => {
      this.actionsForms.value.forEach((action, index) => {
        const chipTextAreaComponents = this.chipTextAreaComponents.filter(
          (component) => component.id === action.formId
        );
        const foundAction = workflow.actions[index];
        chipTextAreaComponents.forEach((chipTextAreaComponent) => {
          const parsedContent = this.parseBodyToGetStepId(
            foundAction?.[chipTextAreaComponent?.inputKey] || ''
          );
          chipTextAreaComponent.body = {
            content: this.parseBody((parsedContent || ''), true),
            chips: this.parseOutputs(),
          };
          chipTextAreaComponent.parseAndSetBody();
        });
      });
    }, 1000);
  }

  patchTriggerInputs(inputs: InputModel[]): void {
    if (!inputs?.length) {
      return;
    }
    const inputsForm = this.workflowVersionForm.get(
      'trigger_data'
    ).get('input_values') as FormGroup;
    inputs.forEach((input) => {
      const key = input.key;
      const value = input.value;
      inputsForm.get(key).patchValue(value);
    });
  }

  parseBodyToGetStepId(body: string): string {
    const outputs = this.parseOutputs();
    const stepId = outputs[0]?.value?.replace('{{', '').replace('}}', '').split('.')[0];
    if (!stepId) {
      return body;
    }
    const regex = new RegExp('step_id', 'g');
    const parsedBody = body.replace(regex, stepId);
    return parsedBody;
  }

  async foundAndSelectTrigger(trigger: TriggerModel): Promise<void> {
    const { category, name } = trigger;
    const foundTriggerCategory = this.filteredTriggers.find(
      (item) => item.category.toLowerCase() === category.toLowerCase()
    );
    const foundTrigger = foundTriggerCategory.data.find(
      (item) => item.name.toLowerCase() === name.toLowerCase()
    );
    await this.selectTrigger(foundTrigger);
  }

  foundAndSelectActions(actions: ActionModel[]): void {
    actions.forEach((action) => {
      const actionKey = action.key;
      if (!actionKey) {
        return;
      }
      const foundConciergeTaskAction = this.filteredActions
        .map((act) => act.data.filter((item) => item.key === actionKey))
        .flat()
        .find((item) => item.key === actionKey);
      this.selectActionType(foundConciergeTaskAction, 0);
    });
  }

  getParsedOutputs(outputs: any[]): FieldOption[] {
    if (!outputs?.length) {
      return [];
    }
    const allOutputs = outputs?.map((output) => output?.array)?.flat();
    return allOutputs?.map((output) => ({
      viewValue: output?.plain_title || output?.title,
      value: output?.key
    }))
  }

  onFormValuesChange(formValues: FormValues[][], actionIndex: number): void {
    const actionForm = this.getActionForm(actionIndex);
    const inputValues = actionForm?.get('input_values') as FormGroup;
    inputValues.patchValue({
      filter_groups: formValues
    });
  }

  async addAttachment(action: any): Promise<void> {
    this.errorMessage = '';
    try {
      const attachment = await this.workflowProvider.addAttachment();
      if (attachment.filename !== '') {
        if (!action?.input_values) {
          action.input_values = {};
        }
        if (!action.input_values?.attachments) {
          action.input_values.attachments = [];
        }
        action.input_values.attachments.push(attachment.url);
        const actionForm = this.actionsForms.value?.find((form) => form.id === action.id);
        actionForm.input_values.attachments = action.input_values.attachments;
        this.createWorkflowVersion();
      } else {
        this.errorMessage = 'Unable to attach photo. Please upload a PNG or JPEG file.';
      }
    } catch (err) {
      this.errorMessage =  err.error ? err.error.message : err.message;
    }
  }

  removeAttachment(action: any, index: number): void {
    action.input_values.attachments.splice(index, 1);
    this.createWorkflowVersion();
  }

  async goToPage(url, params, component) {
    if (this.isRightSideContent) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        return this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  /**
   * Handle option change
   * @param action - The action
   * @param actionIndex - The action index
   * @param formControl - The form control
   * @param input - The input
   */
  async onOptionChange(
    action: Action,
    actionIndex: number,
    formControl: FormGroup,
    input: ActionInput
  ): Promise<void> {
    if (input.key === 'guest_reservation_source_id') {
      await this.handleGuestReservationSourceUpdate(
        action,
        actionIndex,
        formControl
      );
    } else if (input.key === 'entity_type_id') {
      await this.handleEntityTypeChange(action, actionIndex);
    } else if (input.key === 'receiver_type') {
      await this.handleReceiverTypeChange(action, actionIndex);
    }
  }

  /**
   * Go to add integration
   */
  goToAddIntegration() {
    this.navCtrl.navigateForward('integration/all');
  }

  /**
   * Handle guest reservation source update
   * @param action - The action
   * @param actionIndex - The action index
   * @param formControl - The form control
   */
  private async handleGuestReservationSourceUpdate(
    action: Action,
    actionIndex: number,
    formControl: any
  ): Promise<void> {
    if (formControl.value === 'add_integration') {
      formControl.patchValue('');
      this.clearEntityTypeFields(action, actionIndex);
      this.goToAddIntegration();
      return;
    }
    const values = this.getActionForm(actionIndex).get('input_values');
    const inputData = await this.fetchGuestReservationSourceTypeEntities(
      values.get('guest_reservation_source_id')?.value || '',
      values.get('entity_type_id')?.value || '',
      action.id
    );
    action.inputData = inputData;
    if (!inputData?.length || inputData?.length === 0) {
      return;
    }
    const entityTypeInput = this.findEntityTypeInput(action);
    const entityTypeOptions = this.mapEntitiesToOptions(inputData);
    entityTypeInput.options = entityTypeOptions;

    // Patch entity type form with first option's value
    const actionForm = this.getActionForm(actionIndex);
    const entityTypeForm = actionForm.get('input_values').get('entity_type_id');
    entityTypeForm.patchValue(entityTypeOptions[0].value);

    const updatedInputData = await this.fetchGuestReservationSourceTypeEntities(
      values.get('guest_reservation_source_id')?.value || '',
      values.get('entity_type_id')?.value || '',
      action.id
    );
    action.inputData = updatedInputData;

    await this.updateEntityTypeFields(actionIndex, updatedInputData);
  }

  /**
   * Clear entity type fields
   * @param action - The action
   * @param actionIndex - The action index
   */
  clearEntityTypeFields(action: Action, actionIndex: number): void {
    const guestReservationSourceIdForm = this.getActionForm(actionIndex)
      .get('input_values')
      .get('guest_reservation_source_id');
    guestReservationSourceIdForm.patchValue('');
    const entityTypeInput = this.findEntityTypeInput(action);
    entityTypeInput.options = [];
    const entityTypeForm = this.getActionForm(actionIndex)
      .get('input_values')
      .get('entity_type_id');
    entityTypeForm.patchValue('');
    const urlForm = this.getActionForm(actionIndex)
      .get('input_values')
      .get('url');
    urlForm.patchValue('');
    const payloadForm = this.getActionForm(actionIndex)
      .get('input_values')
      .get('payload');
    payloadForm.patchValue('');
    const formId = this.getActionForm(actionIndex).get('formId').value;
    const chipTextAreaComponents = this.chipTextAreaComponents.filter(
      (component) => component.id === formId
    );
    chipTextAreaComponents.forEach((chipTextAreaComponent) => {
      chipTextAreaComponent.body = {
        ...chipTextAreaComponent.body,
        content: '',
      };
      chipTextAreaComponent.parseAndSetBody();
    });
  }

  /**
   * Handle receiver type change
   * @param action - The action
   * @param actionIndex - The action index
   */
  private async handleReceiverTypeChange(
    action: Action,
    actionIndex: number
  ): Promise<void> {
    const values = this.getActionForm(actionIndex).get('input_values');
    const inputData = await this.fetchReceiverTypeInputData(
      values.get('receiver_type')?.value || '',
      action.id
    );
    action.inputData = inputData;

    await this.updateReceiverTypeFields(actionIndex, inputData);
  }

    /**
   * Update entity type fields
   * @param actionIndex - The action index
   * @param inputData - The input data
   */
    private async updateReceiverTypeFields(
      actionIndex: number,
      inputData: any[]
    ): Promise<void> {
      const actionForm = this.getActionForm(actionIndex);
      const inputValues = actionForm.get('input_values');

      const action = this.selectedActions[actionIndex];
      if (!action) {
        return;
      }
      // selectActionType overwrites selectedActions[i] with the action TYPE
      // object, which has no input_values. Match the addAttachment pattern
      // and initialize on-demand so the option-refresh + form patches below
      // can still run for newly-added actions.
      action.input_values = action.input_values || {};

      const toInput = inputData.find((item) => item.input_key === 'to');
      if (toInput) {
        action.input_values['to'] = toInput.input_default_value;
        inputValues.get('to')?.patchValue(toInput.input_default_value);
        await this.updateChipTextArea(
          actionIndex,
          'to',
          toInput.input_default_value
        );
      }

      const toCustomerMembersInput = inputData.find(
        (item) => item.input_key === 'to_customer_members'
      );
      if (toCustomerMembersInput) {
        const targetInput = action.inputs?.find(
          (input) => input.key === 'to_customer_members'
        );
        if (targetInput) {
          targetInput.options = (toCustomerMembersInput.input_options || []).map(
            (option) => ({
              ...option,
              value: option.value,
              viewValue: option.label,
            })
          );
        }
        // The backend uses '{{hidden_input}}' as a sentinel for non-applicable
        // hidden fields, and '' as the cleared default for the multi_select;
        // both must become [] for a typed array control so save-draft persists
        // the right shape and publish doesn't crash casting "" to array.
        const defaultValue = toCustomerMembersInput.input_default_value;
        const newValue =
          defaultValue === '' || defaultValue === '{{hidden_input}}'
            ? []
            : defaultValue;
        action.input_values['to_customer_members'] = newValue;
        inputValues.get('to_customer_members')?.patchValue(newValue);
      }
    }

  /**
   * Handle entity type change
   * @param action - The action
   * @param actionIndex - The action index
   */
  private async handleEntityTypeChange(
    action: Action,
    actionIndex: number
  ): Promise<void> {
    const values = this.getActionForm(actionIndex).get('input_values');
    const inputData = await this.fetchGuestReservationSourceTypeEntities(
      values.get('guest_reservation_source_id')?.value || '',
      values.get('entity_type_id')?.value || '',
      action.id
    );
    action.inputData = inputData;
    await this.updateEntityTypeFields(actionIndex, inputData);
  }

  /**
   * Fetch guest reservation source type entities
   * @param value - The value
   * @param entityType - The entity type
   * @param actionId - The action id
   * @returns The guest reservation source type entities
   */
  private async fetchGuestReservationSourceTypeEntities(
    value: any,
    entityType: string,
    actionId: string
  ): Promise<EntityType[]> {
    return await this.workflowProvider.getInputData(
      actionId,
      'Action',
      JSON.stringify({
        guest_reservation_source_id: value,
        entity_type_id: entityType,
      })
    );
  }

  /**
   * Fetch receiver type entities
   * @param value - The value
   * @param actionId - The action id
   * @returns The input data
   */
  private async fetchReceiverTypeInputData(value: any, actionId: string): Promise<EntityType[]> {
    return await this.workflowProvider.getInputData(
      actionId,
      'Action',
      JSON.stringify({
        receiver_type: value,
      })
    );
  }

  /**
   * Find entity type input
   * @param action - The action
   * @returns The entity type input
   */
  private findEntityTypeInput(action: Action): ActionInput {
    return action.inputs.find((input) => input.key === 'entity_type_id');
  }

  /**
   * Map entities to options
   * @param data - The data
   * @returns The entities options
   */
  private mapEntitiesToOptions(data: any[]): EntityType[] {
    const entities = data.find((item) => item.input_key === 'entity_type_id');
    return entities.input_options.map((entity) => ({
      ...entity,
      viewValue: entity.label,
      value: entity.value,
    }));
  }

  /**
   * Update entity type fields
   * @param actionIndex - The action index
   * @param inputData - The input data
   */
  private async updateEntityTypeFields(
    actionIndex: number,
    inputData: any[]
  ): Promise<void> {
    const actionForm = this.getActionForm(actionIndex);
    const inputValues = actionForm.get('input_values');

    const urlInput = inputData.find((item) => item.input_key === 'url');
    const payloadInput = inputData.find((item) => item.input_key === 'payload');

    const triggerStepId = this.latestVersion?.workflow_version_trigger?.step_id;

    // Update URL field
    const urlForm = inputValues.get('url');
    const urlValue = urlInput.input_default_value?.replace(/\{\{\d+\./g, `{{${triggerStepId}.`);
    urlForm.patchValue(urlValue);
    await this.updateChipTextArea(
      actionIndex,
      'url',
      urlValue
    );

    // Update payload field
    const payloadForm = inputValues.get('payload');
    const payloadValue = this.formatJsonString(
      payloadInput.input_default_value?.replace(/\{\{\d+\./g, `{{${triggerStepId}.`)
    );
    payloadForm.patchValue(payloadValue);
    await this.updateChipTextArea(actionIndex, 'payload', payloadValue);
  }

  /**
   * Update chip text area
   * @param actionIndex - The action index
   * @param inputKey - The input key
   * @param content - The content
   */
  private async updateChipTextArea(
    actionIndex: number,
    inputKey: string,
    content: string
  ): Promise<void> {
    const formId = this.getActionForm(actionIndex).get('formId').value;
    const chipTextAreaComponents = this.chipTextAreaComponents.filter(
      (component) => component.id === formId
    );

    const chipTextAreaComponent = chipTextAreaComponents.find(
      (component) => component.inputKey === inputKey
    );

    if (chipTextAreaComponent) {
      chipTextAreaComponent.body = {
        chips: this.parseOutputs(),
        content,
      };
      chipTextAreaComponent.parseAndSetBody();
    }
  }

  /**
   * Get input hint, replace <a href with <a target="_blank" href
   * @param action - The action
   * @param input - The input
   * @returns The input hint
   */
  getInputHint(action: any, input: any): string {
    const inputKey = input?.key;
    const inputData = action?.inputData?.find(
      (item) => item?.input_key === inputKey
    );
    let inputHint = inputData?.input_hint;
    if (inputHint && inputHint.includes('<a href')) {
      inputHint = inputHint.replace('<a href', '<a target="_blank" href');
    }
    return inputHint || '';
  }

  /**
   * Get input default value
   * @param action - The action
   * @param input - The input
   * @returns The input default value
   */
  getInputDefaultValue(action: any, input: any): string {
    const inputData = action?.inputData?.find(
      (item) => item?.input_key === input.key
    );

    return inputData?.input_default_value || '';
  }

  hideInput(action: any, input: any): boolean {

    const inputValue = action?.input_values?.[input.key];
    if (inputValue === '{{hidden_input}}') {
      return true;
    }

    return this.getInputDefaultValue(action, input) === '{{hidden_input}}';
  }

  /**
   * Safely parse JSON string with error handling
   * @param jsonString - The JSON string to parse
   * @returns Parsed object or null if parsing fails
   */
  parseJsonString(jsonString: string): any {
    if (!jsonString || typeof jsonString !== 'string') {
      return null;
    }

    try {
      return JSON.parse(jsonString);
    } catch (_error) {
      return null;
    }
  }

  /**
   * Format object to JSON string with proper indentation
   * @param obj - The object to stringify
   * @returns Formatted JSON string
   */
  formatJsonString(obj: any): string {
    if (!obj) {
      return '';
    }

    try {
      // If obj is already a string, check if it's valid JSON first
      if (typeof obj === 'string') {
        // Try to parse as JSON, if it fails, return the original string
        try {
          const parsed = JSON.parse(obj);
          return JSON.stringify(parsed, null, 2);
        } catch (_parseError) {
          // If it's not valid JSON, return the original string
          return obj;
        }
      }

      return JSON.stringify(obj, null, 2);
    } catch (_error) {
      return '';
    }
  }
}
